<template>
  <div class="signature-pad">
    <canvas
      ref="canvas"
      class="signature-canvas border rounded w-100"
      :style="{ height: `${height}px`, touchAction: 'none' }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @pointerleave="onPointerUp"
    />
  </div>
</template>

<script>
export default {
  name: 'SignaturePad',
  props: {
    height: {
      type: Number,
      default: 220
    },
    lineWidth: {
      type: Number,
      default: 2
    },
    strokeStyle: {
      type: String,
      default: '#111827'
    }
  },
  data() {
    return {
      strokes: [],
      drawing: false,
      activePointerId: null,
      pointerType: null,
      lastSize: { width: 0, height: 0 },
      devicePixelRatio: 1,
      resizeObserver: null
    }
  },
  mounted() {
    this.resizeCanvas()
    this.bindResizeObserver()
    window.addEventListener('resize', this.resizeCanvas, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCanvas)
    this.unbindResizeObserver()
  },
  methods: {
    bindResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return

      const canvas = this.$refs.canvas
      if (!canvas) return

      this.resizeObserver = new ResizeObserver(() => {
        this.resizeCanvas()
      })
      this.resizeObserver.observe(canvas)
    },
    unbindResizeObserver() {
      if (!this.resizeObserver) return
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    },
    resizeCanvas(retryCount = 0) {
      const canvas = this.$refs.canvas
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const cssWidth = Math.max(1, Math.floor(canvas.clientWidth || rect.width))
      const cssHeight = Math.max(
        1,
        Math.floor(canvas.clientHeight || rect.height || this.height)
      )

      if ((cssWidth < 10 || cssHeight < 10) && retryCount < 5) {
        window.requestAnimationFrame(() => this.resizeCanvas(retryCount + 1))
        return
      }

      const dpr = window.devicePixelRatio || 1
      this.devicePixelRatio = dpr

      canvas.width = Math.floor(cssWidth * dpr)
      canvas.height = Math.floor(cssHeight * dpr)

      this.lastSize = { width: cssWidth, height: cssHeight }

      const ctx = canvas.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      this.redraw()
    },
    getCanvasCssSize() {
      return { ...this.lastSize }
    },
    getPointFromEvent(e) {
      const canvas = this.$refs.canvas
      const rect = canvas.getBoundingClientRect()
      const cachedSize = this.getCanvasCssSize()
      const width = Math.max(
        1,
        cachedSize.width || canvas.clientWidth || rect.width
      )
      const height = Math.max(
        1,
        cachedSize.height || canvas.clientHeight || rect.height
      )

      // Converte coordenadas da tela (clientX/Y) para coordenadas do canvas (CSS px),
      // evitando drift quando o elemento sofre escala/zoom/layout do modal.
      const scaleX = rect.width ? width / rect.width : 1
      const scaleY = rect.height ? height / rect.height : 1

      const x = Math.max(0, Math.min(width, (e.clientX - rect.left) * scaleX))
      const y = Math.max(0, Math.min(height, (e.clientY - rect.top) * scaleY))

      const nx = x / width
      const ny = y / height

      return {
        x,
        y,
        nx,
        ny,
        t: Date.now()
      }
    },
    onPointerDown(e) {
      if (this.drawing) return

      this.pointerType = e.pointerType || null
      this.activePointerId = e.pointerId
      this.drawing = true

      const canvas = this.$refs.canvas
      canvas.setPointerCapture?.(e.pointerId)

      const p = this.getPointFromEvent(e)
      this.strokes.push([p])
      this.drawLastSegment()
    },
    onPointerMove(e) {
      if (!this.drawing || e.pointerId !== this.activePointerId) return

      const currentStroke = this.strokes[this.strokes.length - 1]
      if (!currentStroke) return

      const p = this.getPointFromEvent(e)
      const last = currentStroke[currentStroke.length - 1]
      if (last && Math.abs(p.x - last.x) < 0.5 && Math.abs(p.y - last.y) < 0.5)
        return

      currentStroke.push(p)
      this.drawLastSegment()
    },
    onPointerUp(e) {
      if (e.pointerId !== this.activePointerId) return

      this.drawing = false
      this.activePointerId = null
    },
    clear() {
      this.strokes = []
      const canvas = this.$refs.canvas
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    },
    isEmpty() {
      return !this.strokes.length || this.strokes.every((s) => s.length < 2)
    },
    redraw() {
      const canvas = this.$refs.canvas
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      this.strokes.forEach((stroke) => {
        if (stroke.length < 2) return

        ctx.beginPath()
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.strokeStyle = this.strokeStyle
        ctx.lineWidth = this.lineWidth

        ctx.moveTo(stroke[0].x, stroke[0].y)
        for (let i = 1; i < stroke.length; i++) {
          ctx.lineTo(stroke[i].x, stroke[i].y)
        }
        ctx.stroke()
      })
    },
    drawLastSegment() {
      const canvas = this.$refs.canvas
      const ctx = canvas.getContext('2d')
      const stroke = this.strokes[this.strokes.length - 1]
      if (!stroke) return

      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = this.strokeStyle
      ctx.lineWidth = this.lineWidth

      const n = stroke.length
      if (n === 1) {
        ctx.beginPath()
        ctx.arc(stroke[0].x, stroke[0].y, this.lineWidth / 2, 0, Math.PI * 2)
        ctx.fillStyle = this.strokeStyle
        ctx.fill()
        return
      }

      const prev = stroke[n - 2]
      const curr = stroke[n - 1]
      ctx.beginPath()
      ctx.moveTo(prev.x, prev.y)
      ctx.lineTo(curr.x, curr.y)
      ctx.stroke()
    },
    getStrokesJson() {
      return this.strokes.map((stroke) =>
        stroke.map(({ nx, ny, t }) => ({ x: nx, y: ny, t }))
      )
    },
    getMeta() {
      const { width, height } = this.getCanvasCssSize()
      return {
        canvasWidth: width,
        canvasHeight: height,
        devicePixelRatio: this.devicePixelRatio,
        pointerType: this.pointerType,
        lineWidth: this.lineWidth,
        strokeStyle: this.strokeStyle
      }
    },
    getSvg() {
      const { width, height } = this.getCanvasCssSize()
      const paths = this.strokes
        .filter((s) => s.length >= 2)
        .map((stroke) => {
          const d = stroke
            .map((p, i) => {
              const x = (p.nx * width).toFixed(2)
              const y = (p.ny * height).toFixed(2)
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
            })
            .join(' ')
          return `<path d="${d}" fill="none" stroke="${this.strokeStyle}" stroke-width="${this.lineWidth}" stroke-linecap="round" stroke-linejoin="round" />`
        })
        .join('')

      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">${paths}</svg>`
    }
  }
}
</script>

<style scoped>
.signature-canvas {
  background: #fff;
}
</style>
